import { useState } from 'react';
import _ from 'lodash';
import {
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import theme from 'theme';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { createNewBudgetCosts } from 'utils/functions/budgetCostsHelpers';
import { budgetCostItems, eduBudgetTypes } from 'constants/eduBudget';
import {
  EDU_BUDGET_TITLE_MAX,
  EDU_BUDGET_DESCRIPTION_MAX,
  EDU_BUDGET_LINK_MAX,
} from 'constants/fieldLimits';
import {
  Portlet,
  PortletToolbar,
  PortletContent,
  PortletFooter,
} from '../../../components/Portlets';

const EduBudgetRequestForm = ({
  formProps: {
    values,
    errors,
    isValid,
    handleBlur,
    touched,
    dirty,
    setFieldValue,
    submitForm,
    handleChange,
    handleReset,
  },
}) => {
  const [selectedBudgetCosts, setSelectedBudgetCosts] = useState([]);
  // const isEduBudgetFormEnabled = new Date().getMonth() !== 11; TODO:  disable edu budget again if necessary

  const onSubmitButtonClick = async () => {
    await submitForm();
  };

  const handleBugdetCostChange = event => {
    const {
      target: { value },
    } = event;
    setSelectedBudgetCosts(typeof value === 'string' ? value.split(',') : value);
  };

  const resetForm = () => {
    setSelectedBudgetCosts([]);
    handleReset();
  };

  const handleSelectChange = event => {
    resetForm();
    setFieldValue('type', event.target.value);
  };

  const onStartDateChange = (startDate, endDate) => {
    setFieldValue('startDate', startDate);
    if (!endDate) {
      endDate = startDate;
      setFieldValue('endDate', endDate);
    } else if (endDate < startDate) {
      endDate = startDate;
      setFieldValue('endDate', endDate);
    }
  };

  const onEndDateChange = (startDate, endDate) => {
    setFieldValue('endDate', endDate);
    if (endDate < startDate) {
      startDate = endDate;
      setFieldValue('startDate', startDate);
    }
  };

  return (
    <Portlet sx={{ borderRadius: '0 0 0.25rem 0.25rem' }} outlined={1} squared={false}>
      <>
        <PortletContent
          sx={{
            padding: '0.5rem 1.5rem',
            background: 'transparent',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography
            variant="body2"
            component="span"
            sx={{
              fontWeight: 600,
              marginBottom: theme.spacing(2),
              marginTop: theme.spacing(2),
              fontSize: '1.125rem',
            }}
          >
            Education Budget Request
          </Typography>
          <FormControl variant="standard" sx={{ minWidth: 120 }}>
            <InputLabel disabled id="select-eduBudgetType-label">
              Type
            </InputLabel>
            <Select
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
                getContentAnchorEl: null,
              }}
              labelId="select-eduBudgetType-label"
              id="select-eduBudgetType"
              value={values.type || ''}
              onChange={handleSelectChange}
              label="Type"
              data-cy="eduBudgetType"
            >
              {eduBudgetTypes.map(type => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {(values.type === 'Conference' || values.type === 'Workshop') && (
            <>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <div>
                  <DatePicker
                    label={
                      values.type === 'Conference'
                        ? 'Conference Start Date (inclusive)'
                        : 'Workshop Start Date (inclusive)'
                    }
                    value={values.startDate ?? null}
                    onChange={value => onStartDateChange(value, values.endDate)}
                    format="dd.MM.yyyy"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: 'standard',
                        margin: 'normal',
                        id: 'start-date',
                        name: 'startDate',
                        error: touched.startDate && Boolean(errors.startDate),
                        helperText: touched.startDate && errors.startDate ? errors.startDate : '',
                        onBlur: handleBlur,
                        onKeyDown: e => {
                          e.preventDefault();
                        },
                        'data-cy': 'startDateTextField',
                        slotProps: {
                          inputLabel: { shrink: true },
                        },
                      },
                      popper: {
                        'data-cy': 'popover',
                      },
                      openPickerButton: {
                        'aria-label': 'change start date',
                        'data-cy': 'startDateIcon',
                      },
                      nextIconButton: {
                        'data-cy': 'startNextMonthSwitch',
                      },
                    }}
                  />
                </div>
                <div>
                  <DatePicker
                    label={
                      values.type === 'Conference'
                        ? 'Conference End Date (inclusive)'
                        : 'Workshop End Date (inclusive)'
                    }
                    value={values.endDate || null}
                    onChange={value => onEndDateChange(values.startDate, value)}
                    format="dd.MM.yyyy"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: 'standard',
                        margin: 'normal',
                        id: 'end-date',
                        name: 'endDate',
                        error: touched.endDate && Boolean(errors.endDate),
                        helperText: touched.endDate && errors.endDate ? errors.endDate : '',
                        onBlur: handleBlur,
                        onKeyDown: e => {
                          e.preventDefault();
                          return false;
                        },
                        slotProps: {
                          inputLabel: { shrink: true },
                        },
                      },
                      popper: {
                        'data-cy': 'popover',
                      },
                      nextIconButton: {
                        'data-cy': 'endNextMonthSwitch',
                      },
                      openPickerButton: {
                        'aria-label': 'change end date',
                        'data-cy': 'endDateIcon',
                      },
                    }}
                  />
                </div>
              </LocalizationProvider>
              <FormControl
                variant="standard"
                size="small"
                sx={{ minWidth: 120, marginTop: theme.spacing(1) }}
              >
                <InputLabel disabled id="budgetCostsLabel">
                  Budget Costs
                </InputLabel>
                <Select
                  labelId="budgetCostsLabel"
                  id="budgetCosts"
                  multiple
                  value={selectedBudgetCosts}
                  onChange={handleBugdetCostChange}
                  renderValue={selected => selected.join(', ')}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    getContentAnchorEl: null,
                  }}
                  onBlur={() =>
                    setFieldValue('budgetCosts', createNewBudgetCosts(selectedBudgetCosts))
                  }
                >
                  {budgetCostItems.map(item => (
                    <MenuItem key={item} value={item}>
                      <Checkbox checked={selectedBudgetCosts.indexOf(item) > -1} color="primary" />
                      {item === 'Enrolment fee' ? (
                        <div style={{ fontSize: '0.938rem' }}>
                          <ListItemText primary={item} />
                          <span>(Final amount may include tax/VAT)</span>
                        </div>
                      ) : (
                        <ListItemText primary={item} />
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
          <TextField
            fullWidth
            id="total-amount"
            label="Preliminary Amount"
            name="totalAmount"
            type="number"
            placeholder="0 EUR"
            value={values.totalAmount || ''}
            onChange={handleChange}
            margin="normal"
            variant="standard"
            error={touched.totalAmount && Boolean(errors.totalAmount)}
            onBlur={handleBlur}
            helperText={errors.totalAmount && touched.totalAmount ? errors.totalAmount : ''}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            fullWidth
            id="title"
            label="Title"
            name="title"
            placeholder="Enter a title"
            variant="standard"
            multiline
            maxRows="4"
            value={values.title || ''}
            error={touched.title && Boolean(errors.title)}
            onBlur={handleBlur}
            margin="normal"
            helperText={
              (values.title || '').length >= EDU_BUDGET_TITLE_MAX
                ? `Maximum length of ${EDU_BUDGET_TITLE_MAX} characters reached.`
                : errors.title && touched.title
                  ? errors.title
                  : ''
            }
            onChange={handleChange}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { maxLength: EDU_BUDGET_TITLE_MAX },
            }}
          />

          <TextField
            fullWidth
            id="link"
            label="Link"
            name="link"
            placeholder="Enter a link"
            variant="standard"
            multiline
            maxRows="4"
            value={values.link || ''}
            error={touched.link && Boolean(errors.link)}
            onBlur={handleBlur}
            helperText={
              (values.link || '').length >= EDU_BUDGET_LINK_MAX
                ? `Maximum length of ${EDU_BUDGET_LINK_MAX} characters reached.`
                : errors.link && touched.link
                  ? errors.link
                  : ''
            }
            onChange={handleChange}
            margin="normal"
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { maxLength: EDU_BUDGET_LINK_MAX },
            }}
          />
          <TextField
            fullWidth
            id="description"
            label="Description"
            name="description"
            placeholder="Enter in a description if you already have the necessary account or some other relevant information"
            multiline
            variant="standard"
            maxRows="4"
            value={values.description || ''}
            error={touched.description && Boolean(errors.description)}
            onBlur={handleBlur}
            helperText={
              (values.description || '').length >= EDU_BUDGET_DESCRIPTION_MAX
                ? `Maximum length of ${EDU_BUDGET_DESCRIPTION_MAX} characters reached.`
                : errors.description && touched.description
                  ? errors.description
                  : ''
            }
            onChange={handleChange}
            margin="normal"
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { maxLength: EDU_BUDGET_DESCRIPTION_MAX },
            }}
          />
        </PortletContent>
        <PortletFooter sx={{ borderTop: 'none', marginTop: '20%' }}>
          <PortletToolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              disabled={!dirty}
              onClick={() => resetForm()}
              variant="contained"
              sx={{
                bgcolor: 'primary.contrastText',
                color: 'common.black',
                '&:hover': { bgcolor: 'common.neutral' },
              }}
            >
              Reset
            </Button>
            <div>
              <Button
                disabled={
                  _.isNil(values.type) ||
                  _.isNil(values.title) ||
                  _.isNil(values.description) ||
                  _.isNil(values.link) ||
                  !isValid
                }
                variant="contained"
                sx={{
                  bgcolor: 'primary.main',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
                onClick={onSubmitButtonClick}
                data-cy="eduBudgetSubmit"
              >
                Submit
              </Button>
            </div>
          </PortletToolbar>
        </PortletFooter>
      </>
    </Portlet>
  );
};

export default EduBudgetRequestForm;
